/**
 * Chat Limit Management
 * 
 * Tracks daily chat usage per user and plan tier
 */

export type PlanTier = 'plus' | 'pro' | 'elite';

export interface ChatLimit {
  plus: number;
  pro: number;
  elite: 'unlimited';
}

export const CHAT_LIMITS: ChatLimit = {
  plus: 10,   // Plus: 10 chats per day
  pro: 80,    // Pro: 80 chats per day
  elite: 'unlimited',
};

/**
 * Get chat limit for a plan tier
 */
export function getChatLimit(tier: PlanTier): number | 'unlimited' {
  return CHAT_LIMITS[tier];
}

/**
 * Get remaining chats for a user today
 */
export function getRemainingChats(uid: string, tier: PlanTier): number | 'unlimited' {
  if (tier === 'elite') {
    return 'unlimited';
  }

  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const key = `chat_count_${uid}_${today}`;
  const count = parseInt(localStorage.getItem(key) || '0', 10);
  const limit = CHAT_LIMITS[tier];

  return Math.max(0, limit - count);
}

/**
 * Check if user can send a chat
 */
export function canSendChat(uid: string, tier: PlanTier): boolean {
  if (tier === 'elite') {
    return true;
  }

  const remaining = getRemainingChats(uid, tier);
  return remaining === 'unlimited' || remaining > 0;
}

/**
 * Increment chat count for today
 */
export function incrementChatCount(uid: string, tier: PlanTier): void {
  if (tier === 'elite') {
    return; // No limit for Elite
  }

  const today = new Date().toISOString().split('T')[0];
  const key = `chat_count_${uid}_${today}`;
  const currentCount = parseInt(localStorage.getItem(key) || '0', 10);
  localStorage.setItem(key, String(currentCount + 1));
}

/**
 * Get chat count for today
 */
export function getChatCountToday(uid: string): number {
  const today = new Date().toISOString().split('T')[0];
  const key = `chat_count_${uid}_${today}`;
  return parseInt(localStorage.getItem(key) || '0', 10);
}

